import { useEffect, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  EnvelopeSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import {
  PublicConsentButton,
  PublicConsentPage,
  brandName,
} from '../components/public-consent-page';
import {
  confirmOptIn,
  getOptInConfirmation,
  requestOptInResend,
} from '../services/public-consent.service';
import type { OptInState, PublicConsentData } from '../types/public-consent.types';

type ViewState = OptInState | 'loading' | 'submitting' | 'error' | 'resent';

export function SubscriptionConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [data, setData] = useState<PublicConsentData<OptInState>>();
  const [state, setState] = useState<ViewState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadedToken, setLoadedToken] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const requestId = useRef(0);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCountdown = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    setCountdown(null);
  };

  useEffect(() => {
    const currentRequest = ++requestId.current;
    stopCountdown();
    setData(undefined);
    setLoadedToken('');
    setErrorMessage('');
    setState('loading');
    if (!token) {
      setState('invalid');
      return;
    }
    void getOptInConfirmation(token)
      .then((response) => {
        if (requestId.current !== currentRequest) return;
        setData(response);
        setLoadedToken(token);
        setState(response.state);
        document.documentElement.lang = response.locale;
      })
      .catch((error: unknown) => {
        if (requestId.current !== currentRequest) return;
        setErrorMessage(error instanceof Error ? error.message : 'Unable to load this link.');
        setState('error');
      });
  }, [token]);

  useEffect(() => {
    if (state === 'confirmed' && data?.redirectUrl) {
      setCountdown(5);
      const timer = setInterval(() => {
        setCountdown((current) => {
          if (current === null || current <= 1) {
            clearInterval(timer);
            if (typeof window !== 'undefined' && data.redirectUrl) {
              try {
                window.location.assign(data.redirectUrl);
              } catch {
                // Ignore navigation errors in testing
              }
            }
            return 0;
          }
          return current - 1;
        });
      }, 1000);
      countdownTimer.current = timer;
      return () => {
        clearInterval(timer);
      };
    }
  }, [state, data?.redirectUrl]);

  const submit = async () => {
    if (state !== 'confirmable' || loadedToken !== token) return;
    const currentRequest = ++requestId.current;
    setState('submitting');
    try {
      const response = await confirmOptIn(token);
      if (requestId.current !== currentRequest) return;
      setData(response);
      setState(response.state);
    } catch (error) {
      if (requestId.current !== currentRequest) return;
      setErrorMessage(
        error instanceof Error ? error.message : 'Unable to confirm your subscription.'
      );
      setState('error');
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = resendEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResendError('Enter a valid email address.');
      return;
    }
    setResendError('');
    setIsResending(true);
    try {
      await requestOptInResend({ email, token });
      setState('resent');
    } catch {
      // Per guide §8.4: Show the "New link requested" state for every submission
      setState('resent');
    } finally {
      setIsResending(false);
    }
  };

  const name = brandName(data?.branding);
  const visibleState = loadedToken && loadedToken !== token ? 'loading' : state;

  return (
    <PublicConsentPage branding={data?.branding}>
      {visibleState === 'loading' ? (
        <div className="public-consent-loading" role="status">
          Loading…
        </div>
      ) : visibleState === 'confirmed' ? (
        <div className="public-consent-result public-consent-result--success">
          <CheckCircleIcon size={54} weight="duotone" />
          <h1>You&apos;re subscribed</h1>
          <p>Thanks for confirming. You&apos;ll now receive emails from {name}.</p>
          {data?.redirectUrl ? (
            <div className="mt-4 flex flex-col items-center gap-2">
              <a className="public-consent-button" href={data.redirectUrl}>
                Continue to {name}
              </a>
              {countdown !== null && countdown > 0 && (
                <>
                  <p className="text-xs text-muted-foreground" aria-live="polite">
                    Taking you to {name} in {countdown} s…
                  </p>
                  <button
                    type="button"
                    onClick={stopCountdown}
                    className="text-xs font-medium text-neutral-600 underline hover:text-neutral-900"
                  >
                    Stay here
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : visibleState === 'invalid' ? (
        <div className="public-consent-result public-consent-result--warning">
          <WarningCircleIcon size={54} weight="duotone" />
          <h1>This link is no longer valid</h1>
          <p>
            It may have expired or already been used. Enter your email address and, if it&apos;s
            waiting for confirmation, we&apos;ll send you a new link.
          </p>
          <form
            onSubmit={handleResend}
            className="mt-4 flex w-full flex-col gap-3 text-left"
            noValidate
          >
            <div>
              <label
                htmlFor="resendEmail"
                className="mb-1 block text-sm font-medium text-foreground"
              >
                Email address
              </label>
              <input
                id="resendEmail"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={resendEmail}
                onChange={(e) => {
                  setResendEmail(e.target.value);
                  setResendError('');
                }}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-foreground placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                aria-invalid={!!resendError}
              />
              {resendError && <p className="mt-1 text-xs text-destructive">{resendError}</p>}
            </div>
            <PublicConsentButton type="submit" disabled={isResending}>
              {isResending ? 'Sending…' : 'Send me a new link'}
            </PublicConsentButton>
          </form>
        </div>
      ) : visibleState === 'resent' ? (
        <div className="public-consent-result public-consent-result--success">
          <EnvelopeSimpleIcon size={54} weight="duotone" />
          <h1>Check your inbox</h1>
          <p>If this address is waiting for confirmation, we&apos;ve sent a new link.</p>
        </div>
      ) : visibleState === 'error' ? (
        <div className="public-consent-result public-consent-result--warning">
          <WarningCircleIcon size={54} weight="duotone" />
          <h1>We couldn&apos;t load this page</h1>
          <p>{errorMessage}</p>
        </div>
      ) : (
        <div className="public-consent-content">
          <h1>Confirm your subscription</h1>
          <p>Confirm that you want to receive marketing emails from {name}.</p>
          <PublicConsentButton disabled={visibleState === 'submitting'} onClick={submit}>
            {visibleState === 'submitting' ? 'Confirming…' : 'Confirm subscription'}
          </PublicConsentButton>
          <p className="public-consent-footnote">
            If you didn&apos;t request this, you can safely close this page.
          </p>
        </div>
      )}
    </PublicConsentPage>
  );
}
